if (!customElements.get('product-accessories')) {
  customElements.define(
    'product-accessories',
    class ProductAccessories extends HTMLElement {
      constructor() {
        super();
        this.selectedAccessories = [];
        this.isUpdating = false;
      }
      
      connectedCallback() {
        if (!this.dataset.initialized) {
          this.dataset.initialized = 'true';
          setTimeout(() => {
            this.init();
          }, 100);
        }
      }

      init() {
        if (this.dataset.initCalled === 'true') {
          return;
        }
        
        // 先清理旧的监听器和定时器（防止重复绑定）
        this.cleanup();
        
        this.dataset.initCalled = 'true';
        
        this.checkboxes = this.querySelectorAll('.product-accessories__checkbox');
        this.quantityInputs = this.querySelectorAll('.product-accessories__quantity-input');
        this.totalElement = this.querySelector('[data-accessories-total]');
        this.productForm = document.getElementById(`product-form-${this.dataset.section}`);
        
        this.updateCurrentVariantId();
        this.bindEvents();
        this.setupVariantListener();
        this.restoreStateFromDOM();
      }

      cleanup() {
        const variantSelects = this.getVariantSelects();
        if (variantSelects && this.boundVariantChangeHandler) {
          variantSelects.removeEventListener('change', this.boundVariantChangeHandler);
          this.boundVariantChangeHandler = null;
        }
        if (this.boundOnVariantChange) {
          document.removeEventListener('product:variant-change', this.boundOnVariantChange);
          this.boundOnVariantChange = null;
        }
        if (this.urlCheckInterval) {
          clearInterval(this.urlCheckInterval);
          this.urlCheckInterval = null;
        }
      }

      updateCurrentVariantId() {
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');
        if (urlVariantId) {
          this.currentVariantId = parseInt(urlVariantId);
        } else {
          const variantSelects = this.getVariantSelects();
          if (variantSelects && variantSelects.currentVariant) {
            this.currentVariantId = parseInt(variantSelects.currentVariant.id);
          } else {
            this.currentVariantId = parseInt(this.dataset.currentVariantId) || 0;
          }
        }
        this.dataset.currentVariantId = this.currentVariantId;
      }

      getVariantSelects() {
        return document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
      }

      bindEvents() {
        this.checkboxes.forEach(checkbox => {
          checkbox.addEventListener('change', this.handleAccessoryChange.bind(this));
          this.setItemSelectionState(this.getItem(checkbox), checkbox.checked);
        });

        this.quantityInputs.forEach(input => {
          input.addEventListener('change', this.handleQuantityChange.bind(this));
          input.addEventListener('input', this.handleQuantityInput.bind(this));
        });

        this.querySelectorAll('.product-accessories__quantity-btn--minus').forEach(btn => {
          btn.addEventListener('click', this.handleQuantityButtonClick.bind(this));
        });
        this.querySelectorAll('.product-accessories__quantity-btn--plus').forEach(btn => {
          btn.addEventListener('click', this.handleQuantityButtonClick.bind(this));
        });
      }

      setupVariantListener() {
        const variantSelects = this.getVariantSelects();
        
        if (variantSelects) {
          this.boundVariantChangeHandler = this.handleVariantSelectsChange.bind(this);
          variantSelects.addEventListener('change', this.boundVariantChangeHandler);
          
          this.boundOnVariantChange = this.onVariantChange.bind(this);
          document.addEventListener('product:variant-change', this.boundOnVariantChange);
          
          this.lastUrl = window.location.href;
          this.urlCheckInterval = setInterval(() => {
            this.checkUrlChange();
          }, 500);
        }
      }

      handleVariantSelectsChange() {
        setTimeout(() => {
          const variantSelects = this.getVariantSelects();
          if (!variantSelects || !variantSelects.currentVariant) {
            return;
          }
          
          const newVariantId = parseInt(variantSelects.currentVariant.id);
          if (newVariantId && newVariantId !== this.currentVariantId) {
            this.updateAccessoriesForVariant(newVariantId);
          }
        }, 50);
      }

      checkUrlChange() {
        const currentUrl = window.location.href;
        if (currentUrl !== this.lastUrl) {
          this.lastUrl = currentUrl;
          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get('variant');
          if (variantId) {
            const newVariantId = parseInt(variantId);
            if (newVariantId !== this.currentVariantId) {
              this.updateAccessoriesForVariant(newVariantId);
            }
          }
        }
      }

      onVariantChange(event) {
        const eventData = event.detail && event.detail.detail ? event.detail.detail : event.detail;
        
        if (eventData && eventData.sectionId !== this.dataset.section) {
          return;
        }

        const newVariant = eventData ? eventData.variant : null;
        if (!newVariant) {
          return;
        }

        const newVariantId = parseInt(newVariant.id);
        if (newVariantId && newVariantId !== this.currentVariantId) {
          this.updateAccessoriesForVariant(newVariantId);
        }
      }

      async updateAccessoriesForVariant(variantId) {
        if (this.isUpdating) {
          return;
        }
        
        if (!variantId || variantId === this.currentVariantId) {
          return;
        }

        this.isUpdating = true;
        
        try {
          const variantSelects = this.getVariantSelects();
          let fetchUrl;
          
          if (variantSelects && variantSelects.dataset.url) {
            fetchUrl = `${variantSelects.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`;
          } else {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variantId);
            url.searchParams.set('section_id', this.dataset.section);
            fetchUrl = url.toString();
          }

          const response = await fetch(fetchUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          const newAccessoriesElement = doc.querySelector(`product-accessories[data-section="${this.dataset.section}"]`);
          
          if (newAccessoriesElement) {
            const hasAccessoriesAttr = newAccessoriesElement.getAttribute('data-has-accessories');
            const hasAccessories = hasAccessoriesAttr === 'true' || hasAccessoriesAttr === '1';

            if (!hasAccessories) {
              // 新变体没有配件：隐藏组件并重置状态
              this.innerHTML = '';
              this.style.display = 'none';
              this.currentVariantId = variantId;
              this.selectedAccessories = [];
              if (this.productForm) {
                this.productForm.dataset.selectedAccessories = JSON.stringify([]);
              }
              return;
            }

            // 保存当前选中的配件
            const currentSelected = this.getSelectedAccessories();
            
            // 更新 data-current-variant-id
            const newVariantIdAttr = newAccessoriesElement.getAttribute('data-current-variant-id');
            if (newVariantIdAttr) {
              this.dataset.currentVariantId = newVariantIdAttr;
              this.currentVariantId = parseInt(newVariantIdAttr);
            }
            
            // 更新内容
            this.innerHTML = newAccessoriesElement.innerHTML;
            
            // 确保显示（完全移除内联样式）
            this.style.removeProperty('display');
            this.style.removeProperty('visibility');
            this.style.removeProperty('opacity');
            
            // 清除初始化标志，强制重新初始化
            delete this.dataset.initCalled;
            delete this.dataset.initialized;
            
            // 重新初始化
            this.init();
            
            // 恢复之前选中的配件
            if (currentSelected.length > 0) {
              setTimeout(() => {
                this.restoreSelectedAccessories(currentSelected);
              }, 200);
            }
          } else {
            // 如果没有配件，隐藏元素
            this.style.display = 'none';
            this.currentVariantId = variantId;
          }
        } catch (error) {
          // Silent fail
        } finally {
          this.isUpdating = false;
        }
      }

      disconnectedCallback() {
        this.cleanup();
      }

      restoreSelectedAccessories(selectedAccessories) {
        selectedAccessories.forEach(selected => {
          const checkbox = this.querySelector(`input[value="${selected.variantId}"]`);
          if (checkbox && !checkbox.disabled) {
            checkbox.checked = true;
            const item = this.getItem(checkbox);
            const quantityInput = item.querySelector('.product-accessories__quantity-input');
            if (quantityInput) {
              quantityInput.value = selected.quantity || 1;
            }
            this.handleAccessoryChange({ target: checkbox });
          }
        });
      }

      restoreStateFromDOM() {
        this.selectedAccessories = [];
        this.checkboxes.forEach(checkbox => {
          if (checkbox.checked) {
            const item = this.getItem(checkbox);
            const variantId = parseInt(checkbox.value);
            const price = parseInt(checkbox.dataset.accessoryPrice || 0);
            const quantityInput = item.querySelector('.product-accessories__quantity-input');
            const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
            
            this.selectedAccessories.push({
              variantId: variantId,
              price: price,
              quantity: quantity
            });
            this.setItemSelectionState(item, true);
          }
        });
        
        if (this.selectedAccessories.length > 0) {
          this.updateTotal();
          this.updateProductForm();
        } else {
          if (this.totalElement) {
            const formattedPrice = this.formatMoney(0);
            this.totalElement.textContent = formattedPrice;
            this.totalElement.dataset.accessoriesTotal = 0;
          }
          if (this.productForm) {
            this.productForm.dataset.selectedAccessories = JSON.stringify([]);
          }
        }
      }

      handleAccessoryChange(event) {
        const checkbox = event.target;
        const variantId = parseInt(checkbox.value);
        const price = parseInt(checkbox.dataset.accessoryPrice || 0);
        const isChecked = checkbox.checked;
        const item = this.getItem(checkbox);

        const quantityInput = item.querySelector('.product-accessories__quantity-input');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

        if (isChecked) {
          const existingIndex = this.selectedAccessories.findIndex(item => item.variantId === variantId);
          if (existingIndex >= 0) {
            this.selectedAccessories[existingIndex].quantity = quantity;
          } else {
            this.selectedAccessories.push({
              variantId: variantId,
              price: price,
              quantity: quantity
            });
          }
        } else {
          this.selectedAccessories = this.selectedAccessories.filter(
            item => item.variantId !== variantId
          );
        }

        this.setItemSelectionState(item, isChecked);
        this.updateTotal();
        this.updateProductForm();
      }

      handleQuantityChange(event) {
        const input = event.target;
        const variantId = parseInt(input.dataset.accessoryVariantId);
        const quantity = parseInt(input.value) || 1;
        const item = this.getItem(input);
        const checkbox = item.querySelector('.product-accessories__checkbox');
        
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || parseInt(input.dataset.accessoryInventoryQuantity) || 99;
        const validQuantity = Math.max(min, Math.min(max, quantity));
        input.value = validQuantity;

        if (checkbox && checkbox.checked) {
          const existingIndex = this.selectedAccessories.findIndex(item => item.variantId === variantId);
          if (existingIndex >= 0) {
            this.selectedAccessories[existingIndex].quantity = validQuantity;
            this.updateTotal();
            this.updateProductForm();
          }
        } else if (checkbox && !checkbox.disabled && validQuantity > 0) {
          checkbox.checked = true;
          this.handleAccessoryChange({ target: checkbox });
        }
      }

      handleQuantityInput(event) {
        const input = event.target;
        const value = parseInt(input.value);
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || parseInt(input.dataset.accessoryInventoryQuantity) || 99;
        
        if (isNaN(value) || value < min) {
          input.value = min;
        } else if (value > max) {
          input.value = max;
        }
      }

      handleQuantityButtonClick(event) {
        event.preventDefault();
        const button = event.target.closest('button');
        const item = this.getItem(button);
        const input = item.querySelector('.product-accessories__quantity-input');
        const checkbox = item.querySelector('.product-accessories__checkbox');
        
        if (!input) return;

        const currentValue = parseInt(input.value) || 1;
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || parseInt(input.dataset.accessoryInventoryQuantity) || 99;
        let newValue = currentValue;

        if (button.classList.contains('product-accessories__quantity-btn--plus')) {
          newValue = Math.min(max, currentValue + 1);
        } else if (button.classList.contains('product-accessories__quantity-btn--minus')) {
          newValue = Math.max(min, currentValue - 1);
        }

        input.value = newValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        if (checkbox && checkbox.checked) {
          this.handleQuantityChange({ target: input });
        } else if (checkbox && !checkbox.disabled && newValue > 0) {
          checkbox.checked = true;
          this.handleAccessoryChange({ target: checkbox });
        }
      }

      getItem(element) {
        return element.closest('.product-accessories__item');
      }

      setItemSelectionState(item, isSelected) {
        if (!item) return;
        const card = item.querySelector('.product-accessories__card');
        if (!card) return;
        card.classList.toggle('is-selected', Boolean(isSelected));
      }

      updateTotal() {
        const total = this.selectedAccessories.reduce((sum, item) => {
          return sum + (item.price * (item.quantity || 1));
        }, 0);
        
        if (this.totalElement) {
          const formattedPrice = this.formatMoney(total);
          this.totalElement.textContent = formattedPrice;
          this.totalElement.dataset.accessoriesTotal = total;
        }
      }

      updateProductForm() {
        if (this.productForm) {
          this.productForm.dataset.selectedAccessories = JSON.stringify(
            this.selectedAccessories.map(item => ({
              variantId: item.variantId,
              quantity: item.quantity || 1
            }))
          );
        }
      }

      getSelectedAccessories() {
        return this.selectedAccessories.map(item => ({
          variantId: item.variantId,
          quantity: item.quantity || 1
        }));
      }

      formatMoney(cents) {
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(cents);
        }
        const amount = (cents / 100).toFixed(2);
        const currencySymbol = window.Shopify?.currency?.active || '$';
        return `${currencySymbol}${amount}`;
      }
    }
  );
}
